import React, { memo, useCallback, useEffect, useRef, useState } from 'react';

import { Box, FormControl, InputBase, TextField as MaterialTextField } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { PendingChangeKind } from 'constants/pendingChanges';
import { resolvePendingChangeKind } from 'utils/pendingChangeKind';

import ConfirmPopperCDS, { ConfirmPopperType } from '../../Popper/ConfirmPopper';
import FieldWrapper from '../FieldWrapper';
import HelperText from '../HelperText';
import Label from '../Label';

import { customHelperStyles, getTextFieldStyles, maskInputStyles } from './TextFieldCDS.styles';
import { getMaskedValue, getSlotProps } from './TextFieldCDS.utils';

const slots = {
  formHelperText: HelperText,
};

const TextFieldCDS = ({
  value = '',
  label = '',
  error = false,
  required = false,
  noValidate = false,
  maxLength,
  multiline = false,
  rows = 1,
  containerClassName = '',
  InputProps,
  inputProps,
  ariaLabel = '',
  helperText = '',
  columns = 1,
  width = '100%',
  withPopupError = false,
  disabled = false,
  customStyles,
  isFieldChanged = false,
  fieldChangeKind = null,
  dataTestId,
  'data-testid': dataTestIdAttr,
  withRequiredIcon = true,
  isMasked = false,
  isMaskFormat = false,
  id,
  name,
  multiple,
  size,
  minValue,
  maxValue,
  maxRows,
  isScrollable = false,
  isHidden = false,
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  onSelect: onSelectProp,
  inputRef: inputRefProp,
  customHelper = null,
  formOnChange,
  // Consumed here so they never reach the spread onto MaterialTextField (and from there the DOM node):
  // both are custom field-config props MUI's TextField forwards to its root element, which React rejects
  // with a "does not recognize the prop" warning.
  withStaticHelper,
  endAdornment,
  ...props
}) => {
  const [isPopperOpen, setIsPopperOpen] = useState(false);

  const fieldId = id || name;
  const changeKind = resolvePendingChangeKind(fieldChangeKind, isFieldChanged);

  const rowsProps = maxRows ? { minRows: rows } : { rows };

  const inputRef = useRef(null);
  const maskRef = useRef(null);
  const maskInputRef = useRef(null);

  const resolvedInputRef = inputRefProp || inputRef;

  const resolvedDataTestId = dataTestId || dataTestIdAttr || null;

  const customHelperNode = _.isFunction(customHelper)
    ? customHelper({
        value,
        name,
        error,
        helperText,
        disabled,
        noValidate,
        onChange: formOnChange,
      })
    : customHelper;

  const onFocus = useCallback(
    e => {
      onFocusProp?.(e);
      setIsPopperOpen(true);
    },
    [onFocusProp],
  );

  const onBlur = useCallback(
    e => {
      onBlurProp?.(e);
      setIsPopperOpen(false);
    },
    [onBlurProp],
  );

  const onSelect = useCallback(
    e => {
      if (maskInputRef.current) {
        maskInputRef.current.scrollLeft = e.target.scrollLeft;
      }

      onSelectProp?.(e);
    },
    [onSelectProp],
  );

  useEffect(() => {
    if (!isMasked || !inputRef.current) {
      return;
    }

    const input = inputRef.current;

    const handleResize = () => {
      if (!maskRef.current) {
        return;
      }

      const inputWidth = input.offsetWidth;
      maskRef.current.style.width = `${inputWidth}px`;
    };

    const resizeObserver = new ResizeObserver(handleResize);

    resizeObserver.observe(input);

    return () => {
      resizeObserver.disconnect();
    };
  }, [isMasked]);

  return (
    <FormControl
      className={containerClassName}
      columns={columns}
      component={FieldWrapper}
      customStyles={customStyles}
      isHidden={isHidden}
      variant="outlined"
      width={width}
    >
      {label && (
        <Label error={error} name={fieldId} required={required} withRequiredIcon={withRequiredIcon}>
          {label}
        </Label>
      )}

      <MaterialTextField
        {...props}
        data-is-changed={changeKind === PendingChangeKind.DIRECT}
        data-testid={resolvedDataTestId}
        disabled={disabled}
        error={error}
        helperText={!withPopupError && !customHelper ? helperText : ''}
        id={fieldId}
        inputRef={resolvedInputRef}
        maxRows={maxRows}
        multiline={multiline}
        name={name}
        {...rowsProps}
        slotProps={getSlotProps({
          InputProps,
          maxLength,
          inputProps,
          ariaLabel,
          error,
          isMaskFormat,
          isMasked,
          changeKind,
          multiple,
          size,
          minValue,
          maxValue,
          isMultiline: multiline,
        })}
        slots={slots}
        sx={getTextFieldStyles({ sx: props.sx, multiline, rows, isScrollable })}
        value={value ?? ''}
        onBlur={onBlur}
        onFocus={onFocus}
        onSelect={onSelect}
      />

      {customHelper && !error && <Box sx={customHelperStyles}>{customHelperNode}</Box>}
      {customHelper && error && !withPopupError && helperText && (
        <Box sx={customHelperStyles}>
          <HelperText error>{helperText}</HelperText>
        </Box>
      )}

      {isMasked && (
        <InputBase
          disabled={disabled}
          inputRef={maskInputRef}
          name={`${name}-mask`}
          ref={maskRef}
          slotProps={getSlotProps({ forMaskInput: true })}
          sx={maskInputStyles}
          value={getMaskedValue({ value, shownChars: 4 })}
        />
      )}

      {withPopupError && (
        <ConfirmPopperCDS
          anchorEl={resolvedInputRef?.current}
          open={isPopperOpen && error}
          text={helperText}
          type={ConfirmPopperType.ERROR}
        />
      )}
    </FormControl>
  );
};

TextFieldCDS.propTypes = {
  name: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
  id: PropTypes.string,
  required: PropTypes.bool,
  error: PropTypes.bool,
  noValidate: PropTypes.bool,
  maxLength: PropTypes.number,
  multiple: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
  helperText: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  multiline: PropTypes.bool,
  rows: PropTypes.number,
  maxRows: PropTypes.number,
  columns: PropTypes.number,
  containerClassName: PropTypes.string,
  InputProps: PropTypes.object,
  inputProps: PropTypes.object,
  width: PropTypes.string,
  withPopupError: PropTypes.bool,
  withRequiredIcon: PropTypes.bool,
  isMaskFormat: PropTypes.bool,
  isMasked: PropTypes.bool,
  disabled: PropTypes.bool,
  isFieldChanged: PropTypes.bool,
  // Why the value is pending: a Direct change is highlighted, a Recalculated one underlined; takes precedence over
  // isFieldChanged.
  fieldChangeKind: PropTypes.oneOf(Object.values(PendingChangeKind)),
  isHidden: PropTypes.bool,
  isScrollable: PropTypes.bool,
  customStyles: PropTypes.object,
  dataTestId: PropTypes.string,
  'data-testid': PropTypes.string,
  size: PropTypes.string,
  inputRef: PropTypes.object,
  minValue: PropTypes.number,
  maxValue: PropTypes.number,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  customHelper: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
  formOnChange: PropTypes.func,
  withStaticHelper: PropTypes.bool,
  endAdornment: PropTypes.node,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onSelect: PropTypes.func,
};

export default memo(TextFieldCDS);
